import countBy from 'ramda/src/countBy';
import filter from 'ramda/src/filter';
import prop from 'ramda/src/prop';
import propEq from 'ramda/src/propEq';
import i18n from 'invision-core/src/components/i18n/i18n';
import { INTERNAL_STATUS } from 'invision-ui/lib/components/messaging/statusIndicator/inventoryInternalStatus/inventory.internal.status.types';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { IsDbss } from 'invision-core/src/components/session/businessunit.selectors';
import { PaymentInstrumentTypeSelector } from 'invision-core/src/components/metadata/codes/codes.selectors.deprecated';
import { UserSecurityAttributesSelector } from 'invision-core/src/components/session/session.selectors';
import { hasAccess, hasAdminAccess } from 'invision-core/src/components/security/permission.service';
import { createSelector } from 'reselect';
import LocaleKeys from '../../locales/keys';
import { MutableTransactionDetailPaymentInstruments } from './customer.transactions.selectors';
import { ConvergentBillerOrderPaymentInstruments } from './customer.convergent.biller.selectors';
import { AvailableTypesMapSelector, IsAllPaymentInstrumentTypesAvailableSelector, MutablePaymentInstrumentsSelector, PaymentInstrumentsSelector, PaymentInstrumentsWithTypeNamesViewModel } from './customer.ewallet.selectors';
import { isPaymentInstrumentTypeAvailable } from './customer.ewallet.selectors.helpers';
import { CurrentMemberHasCreatePaymentInstrumentPermissionsSelector } from '../../reducers/selectors/customer.household.selectors';
import { E_WALLET_ADMIN_ACCESS, ISSUE_CREDIT_TO_ALTERNATE_PAYMENT_INSTRUMENT, REFUND_LIMIT_AND_ACCESS, RETURN_DEVICE_ACCESS } from '../../security.attributes';
import { NEW_PAYMENT_INSTRUMENT, REFUND_BY_CHECK } from '../../components/customer/applyCreditModal/apply.credit.modal.constants';
import { PaymentInstrumentConfigurationSelector } from './modify.payment.method.popup.selectors';
import { isCreatablePaymentInstrumentType } from '../helpers/ewallet.reducer.helper';
import { SUPPORTED_PAYMENT_INSTRUMENT_TYPES } from '../../components/customer/ewallet/ewallet.constants';
import { SubscriberDeviceFinancingWidgetActiveDeviceDetailsSelector } from './customer.devices.selectors';
import { IsPIIDataAccessEnabledSelector } from './customer.selectors';
export var MutableCurrentPaymentInstrumentsSelector = createSelector([MutableTransactionDetailPaymentInstruments, ConvergentBillerOrderPaymentInstruments], function (transactionPaymentInstruments, orderDetailPaymentInstruments) {
  var paymentInstruments = transactionPaymentInstruments && transactionPaymentInstruments.length > 0 ? transactionPaymentInstruments : [];
  paymentInstruments = paymentInstruments && orderDetailPaymentInstruments && orderDetailPaymentInstruments.length > 0 ? orderDetailPaymentInstruments : paymentInstruments;
  return paymentInstruments;
});
export var CurrentPaymentInstrumentsTypeSelector = createSelector([MutableCurrentPaymentInstrumentsSelector], function (paymentInstruments) {
  return paymentInstruments.map(function (_ref) {
    var Type = _ref.Type;
    return Type;
  });
});
export var CanAddPaymentInstruments = createSelector([CurrentMemberHasCreatePaymentInstrumentPermissionsSelector, IsPIIDataAccessEnabledSelector, UserSecurityAttributesSelector], function (currentSubHasEditPermission, isPIIDataAccessEnabled, userSecurityAttributes) {
  return hasAdminAccess(userSecurityAttributes, E_WALLET_ADMIN_ACCESS) && currentSubHasEditPermission && isPIIDataAccessEnabled;
});
export var CanCreateCurrentPaymentInstrumentTypes = createSelector([PaymentInstrumentsSelector, CurrentPaymentInstrumentsTypeSelector, PaymentInstrumentConfigurationSelector], function (paymentInstruments, paymentInstrumentTypes, paymentInstrumentConfigurations) {
  var paymentInstrumentTypeCounts = countBy(prop('Type'))(paymentInstruments);
  var activePaymentInstrumentTypeCounts = countBy(prop('Type'))(filter(propEq(1, 'Status'))(paymentInstruments));
  var paymentInstrumentTypesCanCreate = paymentInstrumentTypes.map(function (type) {
    var typeCount = paymentInstrumentTypeCounts[type];
    var typeActiveCount = activePaymentInstrumentTypeCounts[type];
    var typeConfiguration = paymentInstrumentConfigurations.find(function (config) {
      return config && Number(config.paymentInstrumentTypeCode) === type;
    });
    var isMax = typeConfiguration && typeConfiguration.alertMax > 0 && typeCount >= typeConfiguration.alertMax;
    var isMaxActive = typeConfiguration && typeConfiguration.alertMaxActive > 0 && typeActiveCount >= typeConfiguration.alertMaxActive;
    var isNotOptedOut = isCreatablePaymentInstrumentType(type);
    return {
      Type: type,
      CanCreate: !(isMax || isMaxActive) && isNotOptedOut
    };
  });
  return paymentInstrumentTypesCanCreate.every(function (type) {
    return type.CanCreate;
  });
});
export var IsLoyaltyAccount = createSelector([MutableCurrentPaymentInstrumentsSelector], function (paymentInstruments) {
  return paymentInstruments.every(function (paymentInstrument) {
    return paymentInstrument && paymentInstrument.StoredValueAccount && paymentInstrument.StoredValueAccount.LoyaltyCard;
  });
});
export var CanChangePaymentInstrument = createSelector([UserSecurityAttributesSelector, IsLoyaltyAccount], function (userSecurityAttributes, isLoyaltyAccount) {
  return hasAccess(userSecurityAttributes, ISSUE_CREDIT_TO_ALTERNATE_PAYMENT_INSTRUMENT) && !isLoyaltyAccount;
});
export var MutablePaymentInstrumentsTypeFilterOptionsSelector = createSelector([MutablePaymentInstrumentsSelector, CanAddPaymentInstruments, CanCreateCurrentPaymentInstrumentTypes, PaymentInstrumentTypeSelector, IsAllPaymentInstrumentTypesAvailableSelector, AvailableTypesMapSelector], function (currentPaymentInstruments, canAddPaymentInstruments, canCreateCurrentPaymentInstrumentTypes, allPaymentInstrumentTypes, isAllPaymentInstrumentTypesAvailable, availablePaymentInstrumentTypes) {
  var currentPaymentInstrumentsFiltered = currentPaymentInstruments.map(function (_ref2) {
    var Id = _ref2.Id,
      Name = _ref2.Name;
    return {
      value: Id,
      text: Name
    };
  });
  if (canAddPaymentInstruments && canCreateCurrentPaymentInstrumentTypes && isPaymentInstrumentTypeAvailable(SUPPORTED_PAYMENT_INSTRUMENT_TYPES.CREDIT_CARD, allPaymentInstrumentTypes, isAllPaymentInstrumentTypesAvailable, availablePaymentInstrumentTypes)) {
    currentPaymentInstrumentsFiltered.push({
      value: NEW_PAYMENT_INSTRUMENT,
      text: i18n.translate(LocaleKeys.ORDER_DETAILS.CREDIT.ADD_NEW_PAYMENT_METHOD)
    });
  }
  return currentPaymentInstrumentsFiltered;
});
export var CanApplyCreditSelector = createImmutableSelector([UserSecurityAttributesSelector], function (userSecurityAttributes) {
  return hasAccess(userSecurityAttributes, REFUND_LIMIT_AND_ACCESS);
});
export var CanReturnDeviceSelector = createImmutableSelector([UserSecurityAttributesSelector, SubscriberDeviceFinancingWidgetActiveDeviceDetailsSelector], function (userSecurityAttributes, deviceDetails) {
  if (deviceDetails && deviceDetails.SubscriberInventory) {
    return hasAccess(userSecurityAttributes, RETURN_DEVICE_ACCESS) && deviceDetails.SubscriberInventory.InventoryInternalStatus === INTERNAL_STATUS.ACTIVE;
  }
  return false;
});
export var ApplyCreditFromOverviewPaymentOptionsSelector = createImmutableSelector([PaymentInstrumentsWithTypeNamesViewModel, PaymentInstrumentTypeSelector, IsAllPaymentInstrumentTypesAvailableSelector, AvailableTypesMapSelector, IsDbss], function (currentPaymentInstruments, allPaymentInstrumentTypes, isAllPaymentInstrumentTypesAvailable, availablePaymentInstrumentTypes, isDbss) {
  var creditCardOrEcheckPaymentInstruments = currentPaymentInstruments.filter(function (paymentInstrument) {
    return "".concat(paymentInstrument.Type) === SUPPORTED_PAYMENT_INSTRUMENT_TYPES.CREDIT_CARD || "".concat(paymentInstrument.Type) === SUPPORTED_PAYMENT_INSTRUMENT_TYPES.E_CHECK || "".concat(paymentInstrument.Type) === SUPPORTED_PAYMENT_INSTRUMENT_TYPES.EXTERNAL_BILL;
  });
  var paymentInstrumentOptions = creditCardOrEcheckPaymentInstruments.map(function (_ref3) {
    var Id = _ref3.Id,
      Name = _ref3.Name;
    return {
      value: Id,
      text: Name
    };
  });
  if (isPaymentInstrumentTypeAvailable(SUPPORTED_PAYMENT_INSTRUMENT_TYPES.CHECK_REFUND, allPaymentInstrumentTypes, isAllPaymentInstrumentTypesAvailable, availablePaymentInstrumentTypes) && isDbss) {
    paymentInstrumentOptions.push({
      value: REFUND_BY_CHECK,
      text: i18n.translate(LocaleKeys.ORDER_DETAILS.CREDIT.REFUND_BY_CHECK)
    });
  }
  return paymentInstrumentOptions;
});